import React, { useEffect, useState, useRef } from 'react';
import { Link } from "react-router-dom";
import { useInView } from 'react-intersection-observer';
import { getLatestStoredImages } from "../utils/bedrockAPI";

const getStatusInfo = (status) => {
  switch (status) {
    case 'POWER_LOSS':
      return { text: 'POWER OUTAGE', className: 'power' };
    case 'ERROR':
      return { text: 'CELL SIGNAL WEAK', className: 'signal' };
    case 'ONLINE':
      return { text: 'ONLINE', className: 'online' };
    case 'BATTERY':
      return { text: 'BATTERY', className: 'battery' };
    default:
      return status ? { text: status, className: 'default' } : null;
  }
};

/** Product watermark from cap architecture: 2CAM = JobSight, 4CAM = JobSight PRO, else Legacy */
const getProductLabel = (architecture) => {
  if (!architecture) return null;
  if (architecture.endsWith('2CAM')) return 'JobSight';
  if (architecture.endsWith('4CAM')) return 'JobSight PRO';
  return 'Legacy';
};

export default function CapPanel({ cap }) {
  const [previewImage, setPreviewImage] = useState(null);
  const hasTriggered = useRef(false);

  const { ref, inView } = useInView({
    threshold: 0,
    triggerOnce: true,
    rootMargin: '50px 0px'
  });

  // Load image when component becomes visible
  useEffect(() => {
    if (inView && !hasTriggered.current && cap.status === "ACTIVE") {
      hasTriggered.current = true;
      loadImage();
    }
  }, [inView, cap]);

  const loadImage = async () => {
    try {
      const savedUrls = await getLatestStoredImages(cap.capid);
      if (savedUrls) {
        let selectedMonitor = Object.keys(savedUrls)[0];
        if (cap.defaultmonitorid) {
          selectedMonitor = cap.defaultmonitorid;
        }
        if (selectedMonitor) {
          setPreviewImage(savedUrls[selectedMonitor]);
        }
      }
    } catch (e) {
      console.error("Error loading cap preview:", e);
    }
  };

  const statusInfo = getStatusInfo(cap.health);
  const productLabel = getProductLabel(cap.architecture);

  return (
    <Link to={`/caps/${cap.capid}`} className="cap-card-link">
      <div ref={ref} data-capid={cap.capid} className="cap-card">
        {previewImage ? (
          <img
            src={previewImage.url}
            alt={`${cap.name} preview`}
            className="cap-card-image"
            loading="lazy"
          />
        ) : (
          <div className="cap-card-placeholder">No Preview Available</div>
        )}

        {/* Motion history pill – expands on hover */}
        <Link
          to={`/caps/${cap.capid}/events`}
          className="cap-card-motion"
          title="Motion history"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="glyphicon glyphicon-time" aria-hidden="true" />
          <span className="cap-card-motion-label">Motion History</span>
        </Link>

        {/* Product watermark */}
        {productLabel && (
          <span className="cap-card-product">{productLabel}</span>
        )}

        {/* Name + status bar */}
        <div className="cap-card-bar">
          <span className="cap-card-name">{cap.name}</span>
          {statusInfo && (
            <span className={`cap-card-status cap-card-status--${statusInfo.className}`}>
              {statusInfo.text}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
